import { Character, Gift, MediaItem, UserProfile, Notification, HistoryDataItem, ChatMessage } from './types';
import { invokeSupabaseFunction, supabase } from '../lib/supabase';
import { SEED_DATA_CHARACTERS } from './seed-data';
import { Dispatch } from 'redux';
import { setUpdateUserProfily } from '../redux/reducers/authSlice';
import { encode8 } from './handleData';

export interface PaginatedResponse<T> {
  data: T[];
  hasMore: boolean;
  nextCursor?: string;
}

export class CharacterAPI {
  static async getCharacters(): Promise<Character[]> {
    try {
      // Specify exactly which fields to select for better performance
      const { data, error } = await supabase
        .from('characters')
        .select();
      
      if (error) {
        console.error('Error fetching characters from Supabase:', error);
        return SEED_DATA_CHARACTERS; // Fallback to seed data if there's an error
      }
      
      return data as Character[];
    } catch (err) {
      console.error('Failed to fetch characters from Supabase:', err);
      return SEED_DATA_CHARACTERS; // Fallback to seed data if there's an exception
    }
  }

  static async getCharacterAffection(characterId: string): Promise<number> {
    try {
      const user = await supabase.auth.getUser();
      if (!user) return 0;

      const { data, error } = await supabase
        .from('user_character_relations')
        .select('affection')
        .eq('user_id', user.data.user?.id)
        .eq('character_id', characterId)
        .single();

      if (error || !data) {
        return 0;
      }

      return data.affection;
    } catch (err) {
      console.error('Failed to fetch character affection:', err);
      return 0;
    }
  }

  static async updateCharacterInteraction(
    characterId: string, 
    interaction: { message?: string, giftId?: string }
  ): Promise<{ affection: number, response?: string, media?: MediaItem }> {
    try {
      const user = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Send interaction to backend to process and return updated affection
      const { data, error } = await supabase
        .rpc('process_character_interaction', {
          p_character_id: characterId,
          p_user_id: user.data.user?.id,
          p_message: interaction.message,
          p_gift_id: interaction.giftId
        });

      if (error) throw error;

      return data;
    } catch (err) {
      console.error('Failed to update character interaction:', err);
      return { affection: 0 };
    }
  }
};

export class UserAPI {
  static async getCurrentUser(
    dispatch: Dispatch<any>,
    be_invited?: string
  ): Promise<UserProfile | null> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return null;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.user.id)
        .single();
      
      if(error?.code === 'PGRST116') {
        let code = encode8(user.user.id);
        const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          id: user.user.id,
          email: user.user.email || null,
          sub_list: [],
          invitation_code: code,
          be_invited: be_invited || null,
          coins: user.user.email ? 400 : 300,
        })
        .select()
        .single();
        if (error) throw error;
        dispatch( setUpdateUserProfily(data) )
        return data;
      }
      if (error) throw error;
      dispatch( setUpdateUserProfily(data) )
      return data;
    } catch (err) {
      console.error('Failed to get current user:', err);
      return null;
    }
  };
  static async updateUserPhone(phone: string): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return false;

      const { data, error } = await supabase.functions.invoke('update-phone', {
        body: {
          userId: user.user.id,
          newPhone: phone
        },
      })
      if(error) throw error;
      return true;
    } catch (err) {
      console.error('Failed Update Phone:', err);
      return false;
    }
  }
  
  static async getUserInvitation():Promise<{email: string, id: number}[] | string> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return "";

      
      let { data: invitation_list, error } = await supabase
        .from('invitation_list')
        .select('*')
        .eq('user_id', user.user.id);

      if(error) throw error.message;
      return invitation_list || [];
    } catch (err) {
      console.error('Failed Update Phone:', err);
      return "";
    }
  }
  
  static async updateUserCacheDatas(
    event: string
  ): Promise<null> {
    try {
      await supabase.functions.invoke('update-user-caches', { body: { event } } );
      return null;
    } catch (err: any) {
      console.error('Failed to get current user:', err.message);
      return null;
    }
  };
}

export class GiftAPI {
  static async getAvailableGifts(): Promise<Gift[]> {
    try {
      const { data, error } = await supabase
        .from('gifts')
        .select('*')
        .order('price', { ascending: true });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to get available gifts:', err);
      return [];
    }
  }

  static async sendGift(characterId: string, giftId: string): Promise<{
    success: boolean,
    affection: number,
    remainingCoins: number
  }> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return { success: false, affection: 0, remainingCoins: 0 };

      const { data, error } = await supabase
        .rpc('send_gift', {
          p_user_id: user.user.id,
          p_character_id: characterId,
          p_gift_id: giftId
        });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to send gift:', err);
      return { success: false, affection: 0, remainingCoins: 0 };
    }
  }
}

export class NotificationAPI {
  static async getNotifications(): Promise<Notification[]> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return [];

      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to get notifications:', err);
      return [];
    }
  }

  static async markNotificationAsRead(notificationId: string): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return false;

      const { error } = await supabase
        .from('user_notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.user.id);

      return !error;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return false;
    }
  }
}

export class PurchaseAPI {
  static async validatePurchase(
    productId: string, 
    receipt: string, 
    platform: 'ios' | 'android'
  ): Promise<{
    success: boolean,
    coins?: number,
    isPremium?: boolean,
    error?: string
  }> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return { success: false, error: 'User not authenticated' };

      const { data, error } = await supabase
        .rpc('validate_purchase', {
          p_user_id: user.user.id,
          p_product_id: productId,
          p_receipt: receipt,
          p_platform: platform
        });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Failed to validate purchase:', err);
      return { success: false, error: 'Failed to validate purchase' };
    }
  }

  static async getProducts(): Promise<{
    coinPackages: { id: string, coins: number, price: number, displayPrice: string }[],
    subscriptions: { id: string, name: string, price: number, displayPrice: string, period: string }[]
  }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*');

      if (error) throw error;

      const coinPackages = data
        .filter(p => p.type === 'coin_package')
        .map(p => ({
          id: p.id,
          coins: p.coins,
          price: p.price,
          displayPrice: p.display_price
        }));

      const subscriptions = data
        .filter(p => p.type === 'subscription')
        .map(p => ({
          id: p.id,
          name: p.name,
          price: p.price,
          displayPrice: p.display_price,
          period: p.period
        }));

      return { coinPackages, subscriptions };
    } catch (err) {
      console.error('Failed to get products:', err);
      return { coinPackages: [], subscriptions: [] };
    }
  }
}

export class ChatsAPI {
  static async getHistoryList(
    page: number, pageSize: number,
    character_id: number | string
  ): Promise<HistoryDataItem[] | null> {
    
    try {
      let start = (page - 1) * pageSize, end = page * pageSize - 1;
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return null;

      let { data: chats_list, error } = await supabase
        .from('chats_list')
        .select('*')
        .eq('user_id', user.user.id)
        .eq('character_id', character_id)
        .order('created_at', { ascending: false })
        .range(start, end)
      if(error) throw error;
      return chats_list;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return null;
    }
  };

  static async saveHistoryCtxs(
    list: HistoryDataItem['content'][], character_id: number | string
  ): Promise<boolean>{
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return false;
      let new_arr = list.map(item=>{
        return {
          user_id: user.user.id, character_id, content: item
        }
      })

      const { data, error } = await supabase
        .from('chats_list')
        .insert(new_arr)
        .select()
      if(error) throw error;
      return data ? true : false;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      return false;
    }
  }
}
